import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { applyTypography, Chip, useT } from '@repo/common';

/**
 * 한 줄 안에서 텍스트가 넘칠 때 자동 줄바꿈 대신 글자 크기를 한 단계 낮춰 맞추는 텍스트.
 * (whitespace-nowrap 상태에서 scrollWidth > clientWidth 이면 넘친 것으로 판단)
 * @component
 * @param {string} baseClass - 기본(큰) 타이포 클래스
 * @param {string} shrunkClass - 한 단계 축소된 타이포 클래스
 */
const AutoFitText = ({ children, baseClass, shrunkClass, className = '', style }) => {
  const ref = useRef(null);
  const [shrink, setShrink] = useState(false);

  // 내용이 바뀌면 기본 크기로 되돌려 다시 측정한다.
  useLayoutEffect(() => {
    setShrink(false);
  }, [children]);

  // 기본 크기에서 넘치면 한 단계만 축소한다. (shrink=true 이후엔 조기 반환되어 무한 루프 방지)
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || shrink) return;
    if (el.scrollWidth - el.clientWidth > 1) {
      setShrink(true);
    }
  });

  // 뷰포트가 바뀌면 기본 크기로 되돌려 다시 측정한다.
  useEffect(() => {
    const onResize = () => setShrink(false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <p
      ref={ref}
      className={`${className} ${shrink ? shrunkClass : baseClass}`}
      style={style}
    >
      {children}
    </p>
  );
};

/**
 * 날짜 슬롯 카드 컴포넌트
 * @component
 * @param {object} slot - 날짜 슬롯 정보 (id, date, time, status)
 * @param {boolean} isSelected - 선택 여부
 * @param {function} onClick - 클릭 핸들러
 * @param {string} variant - 'modal' | 'workshop' (기본값: 'workshop')
 */
const DateSlotCard = ({
  slot,
  isSelected,
  onClick,
  variant = 'workshop',
  disabled = false,
}) => {
  const t = useT();
  const isModal = variant === 'modal';
  const isPast = slot?.isPast === true;
  const isDisabled = disabled || isPast;
  const dateSlotStatus = String(slot?.dateSlotStatus || '').toUpperCase();
  const statusLabelMap = {
    ONGOING: t('Ongoing', '진행 중'),
    CLOSED: t('Closed', '마감'),
    COMPLETED: t('Completed', '종료'),
  };
  const dateSlotStatusLabel = statusLabelMap[dateSlotStatus];
  // 백엔드 데이터에 available이 없을 수 있어 방어 처리
  const isAvailable = !isPast && (slot?.available !== undefined && slot?.available !== null
    ? slot.available !== false
    : slot?.status !== 'Full');

  // 날짜/시간 텍스트가 길어 카드 영역을 넘치면 한 단계 작은 타이포로 낮춘다.
  const dateBaseClass = isModal
    ? `${applyTypography('b16Strong')} md:${applyTypography('b18Strong')}`
    : `${applyTypography('b16Strong')} tabletH:${applyTypography('b20Strong')}`;
  const dateShrunkClass = isModal
    ? `${applyTypography('b14Strong')} md:${applyTypography('b16Strong')}`
    : `${applyTypography('b14Strong')} tabletH:${applyTypography('b18Strong')}`;
  const timeBaseClass = isModal
    ? applyTypography('b14')
    : `${applyTypography('b14')} tabletH:${applyTypography('b16')}`;
  const timeShrunkClass = isModal
    ? applyTypography('b13')
    : `${applyTypography('b13')} tabletH:${applyTypography('b14')}`;

  return (
    <div
      onClick={(e) => {
        if (isDisabled) return;
        // 카드 선택 클릭이 모달 오버레이/카드 네비게이션으로 전파돼 모달이 닫히는 것 방지
        e.stopPropagation();
        onClick(slot.id, isAvailable);
      }}
      className={classNames(
        'flex flex-col items-start justify-between overflow-clip border-[1.6px] border-solid transition-all shrink-0',
        isDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        {
          // Workshop variant (Figma 디자인 기준)
          'flex-[1_0_calc(50%-4px)] h-[94px] max-w-none min-w-0 px-[16px] py-[12px] rounded-[15px] bg-transparent tabletH:h-[120px] tabletH:w-[274px] tabletH:flex-none tabletH:px-[30px] tabletH:py-[16px]': !isModal,
          // Modal variant (홀수 개일 때 마지막 카드가 늘어나지 않도록 grow=0)
          'flex-[0_0_calc(50%-4px)] h-[94px] max-w-none min-w-0 px-[16px] py-[12px] rounded-[15px] bg-transparent md:flex-[0_0_calc(50%-6px)] md:h-[110px] md:max-w-[286px] md:min-w-[180px] md:px-[24px] md:py-[16px] md:rounded-[20px]': isModal,
          // Border colors
          'border-green-deep02': isSelected && !isDisabled,
          'border-grayscale-gray03': !isSelected || isDisabled,
        }
      )}
    >
      <div className='flex flex-col items-start w-full not-italic whitespace-nowrap shrink-0'>
        <AutoFitText
          className='text-text-primary shrink-0 w-full'
          baseClass={dateBaseClass}
          shrunkClass={dateShrunkClass}
          style={{ lineHeight: '1.5' }}
        >
          {slot.date}
        </AutoFitText>
        <AutoFitText
          className='text-text-secondary shrink-0 w-full'
          baseClass={timeBaseClass}
          shrunkClass={timeShrunkClass}
          style={{ lineHeight: !isModal ? '1.5' : '1.4' }}
        >
          {slot.time}
        </AutoFitText>
      </div>
      <div className='flex items-center gap-[4px] shrink-0'>
        <Chip
          type='fill'
          state='disabled'
          size='xs'
          hideIcon
          disableTypography
          disableDefaultColors
          bgclass='h-auto shrink-0 rounded-[6px] bg-purple-purple01 px-[6px] py-[2px]'
          borderclass='ring-0'
          textclass={`${applyTypography('b12Strong')} text-text-accent`}
        >
          {slot.status}
        </Chip>
        {dateSlotStatusLabel && (
          <Chip
            type='fill'
            state='disabled'
            size='xs'
            hideIcon
            disableTypography
            disableDefaultColors
            bgclass='h-auto shrink-0 rounded-[6px] bg-grayscale-gray02 px-[6px] py-[2px]'
            borderclass='ring-0'
            textclass={`${applyTypography('b12Strong')} text-text-secondary`}
          >
            {dateSlotStatusLabel}
          </Chip>
        )}
      </div>
    </div>
  );
};

export default DateSlotCard;
